/**
 * this component will display the card summary item information
 *
 * @module views/components/SummaryItemCard
 * @memberof -Common
 */
import './SummaryItemCard.scss';

import React from 'react';

import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Icon from '@ulta/core/components/Icon/Icon';
import InlineMessage from '@ulta/core/components/InlineMessage/InlineMessage';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { DXL_NAVIGATION_TYPE } from '@ulta/core/utils/constants/action';
import { spreadProps } from '@ulta/core/utils/dom/dom';


/**
 * Represents a SummaryItemCard component
 *
 * @method
 * @param {SummaryItemCardProps} props - React properties passed from composition
 * @returns SummaryItemCard
 */
export const SummaryItemCard = function( props ){
  const { action, action2, description, image, icon, infoMessage, onClick, title, subTitle } = props;
  const hasLinkAction = action?.navigationType === DXL_NAVIGATION_TYPE.Redirect;

  const handleActionClick = ( e ) => {
    onClick?.( action, e );
  };

  const handleAction2Click = ( e ) => {
    onClick?.( action2, e );
  };

  return (
    <div className='SummaryItemCard'>
      <div className='SummaryItemCard__Content'>
        { image && (
          <UltaAsset { ...image }
            aspectRatio='1:1'
            width={ 64 }
            height={ 64 }
          />
        ) }
        { icon && (
          <Icon
            name={ icon }
            size='custom'
            className='SummaryItemCard__Icon'
          />
        ) }
        { title && (
          <div className='SummaryItemCard__Content__Title'>
            <Text htmlTag='h1'
              color='neutral-900'
              textStyle='body-xlg'
            >
              { title }
            </Text>
          </div>
        ) }
        { subTitle && (
          <Text textStyle='body-2'
            color='neutral-600'
            textAlign='center'
          >
            { subTitle }
          </Text>
        ) }
        { description && (
          <div className='SummaryItemCard__Content__Description'>
            <Text textStyle='body-2'
              color='neutral-900'
              textAlign='center'
            >
              { description }
            </Text>
          </div>
        ) }
        { infoMessage && (
          <div className='SummaryItemCard__Content__message'>
            <InlineMessage
              { ...spreadProps( { ...infoMessage } ) }
              messageType='infocircle'
              role='alert'
            />
          </div>
        ) }
      </div>
      { action?.label && (
        <>
          <div className='SummaryItemCard__Actions'>
            { hasLinkAction ? (
              <Link_Huge
                likeButtonSecondary={ true }
                url={ action.url }
                action={ action }
              >
                { action.label }
              </Link_Huge>
            ) : (
              <Button
                ariaLabel={ action.label }
                fullWidth={ true }
                action={ action }
                label={ action.label }
                onClick={ handleActionClick }
              />
            ) }
          </div>
        </>
      ) }
      { action2?.label && (
        <div className='SummaryItemCard__SecondaryAction'>
          <Button
            ariaLabel={ action2.label }
            fullWidth={ true }
            action={ action2 }
            label={ action2.label }
            variant={ action2.variant }
            onClick={ handleAction2Click }
          />
        </div>
      ) }
    </div>
  );
};

/**
 * Property type definitions
 * @typedef SummaryItemCardProps
 * @type {object}
 * @property {object} [action] - An action object for the primary button/link.
 * @property {object} [action2] - An action object for the secondary button.
 * @property {string} [description] - The main description text.
 * @property {string} [icon] - Sets the name of an icon to display. Ignored if `image` is provided.
 * @property {object} [image] - Sets value for an image.
 * @property {object} [infoMessage] - An object to configure an `InlineMessage`.
 * @property {function} [onClick] - Click handler for the action buttons. Receives `(action, event)`.
 * @property {string} [subTitle] - Sets value for the subTitle.
 * @property {string} [title] - Sets value for the title.
 */
export const propTypes =  {
  action: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string,
    navigationType: PropTypes.string,
    clientActionType: PropTypes.string
  } ),
  action2: PropTypes.object,
  description: PropTypes.string,
  icon: PropTypes.string,
  image: PropTypes.object,
  infoMessage: PropTypes.object,
  onClick: PropTypes.func,
  subTitle: PropTypes.string,
  title: PropTypes.string
};

SummaryItemCard.propTypes = propTypes;

export default SummaryItemCard;